/**
 * Every user-facing string lives in the JSON files beside this one (PRD section 19:
 * "Every string external from day one"). English ships first; Bemba and Nyanja
 * (P1) are added as `bem.json` and `ny.json` with the same keys, written by native
 * speakers. Amounts are never translated: they are formatted by src/lib/money.ts and
 * passed in as interpolation values.
 *
 * Keys are grouped by area: `app` (public shell), `shell` (signed-in shell), `common`,
 * `dates`, `money`, `kit` (shared components), `welcome` and `calculator` in en.json,
 * the default namespace. Signed-in features have their own namespace and file, so the
 * member and officer screens can grow without touching each other:
 * `useTranslation('auth')` (auth.json), `'member'` (member.json) and `'officer'`
 * (officer.json). Keys are type-checked, so a typo fails `npm run typecheck`.
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import auth from './auth.json'
import en from './en.json'
import type member from './member.json'
import type officer from './officer.json'

export const defaultNS = 'translation'

/** Bundled with the app: the shell, the kit, the calculator and sign-in. */
export const resources = {
  en: { translation: en, auth },
} as const

/** Every namespace, for the typed t() keys; member and officer load with their first screen. */
export type Catalogues = (typeof resources)['en'] & { member: typeof member; officer: typeof officer }

const lazyCatalogues = {
  member: () => import('./member.json'),
  officer: () => import('./officer.json'),
} as const

export type LazyNamespace = keyof typeof lazyCatalogues

/**
 * Loads a feature's strings before its first screen renders, so members never download the
 * officer catalogue and the first page (the calculator, say) stays small on mobile data.
 */
export async function loadNamespace(ns: LazyNamespace): Promise<void> {
  if (i18n.hasResourceBundle('en', ns)) return
  const { default: catalogue } = await lazyCatalogues[ns]()
  i18n.addResourceBundle('en', ns, catalogue, true, true)
}

export const SUPPORTED_LANGUAGES = ['en'] as const
export type Language = (typeof SUPPORTED_LANGUAGES)[number]

void i18n.use(initReactI18next).init({
  resources,
  lng: 'en',
  fallbackLng: 'en',
  supportedLngs: SUPPORTED_LANGUAGES,
  defaultNS,
  ns: ['translation', 'auth'],
  // Synchronous: resources are bundled, so the first render already has its strings.
  initAsync: false,
  interpolation: {
    // React escapes output already.
    escapeValue: false,
  },
  returnNull: false,
})

export default i18n
